import React from 'react';
import { Button, Dropdown, Menu, Modal, Tooltip } from 'antd';
import { MergeCellsOutlined, EditOutlined, EllipsisOutlined } from '@ant-design/icons';
import EditLineDialog, { EditableField } from 'components/Inventory/WarehouseTransferErpProcess/EditLineDialog';
import { EPTLineActionType, /* SourceType */ } from 'components/Inventory/WarehouseTransferErpProcess';
import {
  CopySpan,
  DataGrid,
  InfoButton,
  Label,
  //SecondaryButton,
  SpaceCell,
  message,
} from 'components/common';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  DATAGRID_CELL_ICON_WIDTH1,
} from 'constants/config';
import { deleteWarehousePostedTransferLine, fetchWarehousePostedTransferDetail } from 'services/inventory';
import { getProfileSettingValue } from 'utils';

export enum TransferLineGridMode {
  View,
  Edit,
  ScanToSubmit,
};

type Props = {
  mode: TransferLineGridMode;
  onLineChanged?: Function;
  onLoadTransfer?: Function;
  transferNumber: number;
  version?: number;
};

const TransferLineGrid = (props: Props) => {
  const { useState } = React;
  const [currentLine, setCurrentLine] = useState<StringKAnyVPair>({});
  const [currentLineActionType, setCurrentLineActionType] = useState<EPTLineActionType>();
  const [editLineDialogDisableFields, setEditLineDialogDisableFields] = useState<EditableField[]>();
  const [inited, setInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lines, setLines] = useState<StringKAnyVPair[]>([]);
  const [lotEnable, setLotEnable] = useState(false);
  const [editLineDialogVisible, setEditLineDialogVisible] = useState(false);
  const [uomEnable, setUomEnable] = useState(false);
  const [version, setVersion] = useState(props.version);

  const columns = [
    {
      header: 'SKU',
      name: 'sku',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.sku);
      },
    },
    {
      header: 'UOM',
      maxWidth: 80,
      name: 'uom',
      render(row: any) {
        const { data } = row;

        return `${data.uom}(${data.uomRate})`;
      },
    },
    {
      header: 'LOT#',
      name: 'lotNumber',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.lotNumber);
      },
    },
    {
      header: '',
      minWidth: 150,
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data, cellProps } = row;

        (data.pickStatus === 1) && (cellProps.className += ' datagrid-cell-bg-green-1');

        return (
          <div className="ant-input-group-wrapper w-none">
            <Button
              disabled={data.pickStatus === 1}
              onClick={() => pickLine(data)}
              size="small"
              type="primary"
            >
              Pick
            </Button>
            <Button
              disabled={data.pickStatus !== 1}
              onClick={() => unpickLine(data)}
              size="small"
            >
              Clear
            </Button>
          </div>
        );
      },
      renderHeader() {
        return (
          <div className="ant-input-group-wrapper w-none">
            <Button
              disabled={isAllLinesPicked()}
              onClick={pickAllLines}
              size="small"
              type="primary"
            >
              Pick All
            </Button>
            <Button
              disabled={isAllLinesUnpicked()}
              onClick={unpickAllLines}
              size="small"
            >
              Clear All
            </Button>
          </div>
        );
      },
    },
    {
      header: 'Pick Status',
      maxWidth: 120,
      //name: 'fromWarehouseCode',
      render(row: any) {
        const { data, cellProps } = row;
        let status = '';

        if (data.pickStatus === 1) {
          cellProps.className += ' datagrid-cell-bg-green-1';
          status = 'Picked';
        }

        return status;
      },
    },
    /*{
      header: 'From Warehouse',
      name: 'fromWarehouseCode',
    },*/
    {
      header: 'From Location',
      name: 'fromLocationCode',
      render(row: any) {
        const { data } = row;

        //return renderLocationCell(SourceType.From, data);
        return isLineEditable() ?
          renderLocationEditCell(data.fromLocationCode, data, [EditableField.ToLocation]) : 
          renderCopyableCell(data.fromLocationCode);
      },
    },
    {
      header: 'Quantity',
      headerAlign: 'start',
      maxWidth: 100,
      name: 'quantity',
      textAlign: 'end',
    },
    /*{
      header: 'To Warehouse',
      name: 'toWarehouseCode',
    },*/
    {
      header: 'To Location',
      name: 'toLocationCode',
      render(row: any) {
        const { data } = row;

        //return renderLocationCell(SourceType.To, data);
        return isLineEditable() ? 
          renderLocationEditCell(data.toLocationCode, data, [EditableField.FromLocation]) : 
          renderCopyableCell(data.toLocationCode);
      },
    },
    /*{
      header: 'Enter By',
      name: 'createBy',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.createBy);
      },
    },*/
    /*{
      header: 'Note',
      name: 'note',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.note);
      },
    },*/
    {
      align: 'left',
      header: 'Action',
      maxWidth: props.mode === TransferLineGridMode.ScanToSubmit ? 90 : undefined,
      minWidth: props.mode === TransferLineGridMode.ScanToSubmit ? 80 : 120,
      name: '',
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data } = row;

        return (<div className={actionWrapperClassName()}>
          {props.mode === TransferLineGridMode.Edit && (<>
            {/*<SecondaryButton
              className="p-0-8"
              onClick={() => openEditLineDialog(EPTLineActionType.Edit, data)}
              size="small"
            >
              <EditOutlined />
            </SecondaryButton>*/}
            <InfoButton
              className="p-0-8"
              disabled={!isLineSplittable(data)}
              //hovertype="secondary"
              onClick={() => openEditLineDialog(EPTLineActionType.Split, data)}
              size="small"
            >
              {isLineSplittable(data) ? (
                <Tooltip title="Split">
                  <i className="bi bi-signpost-split" />
                </Tooltip>
              ) : (
                <i className="bi bi-signpost-split" />
              )}
            </InfoButton>
          </>)}
          <Dropdown
            overlay={(
              <Menu>
                {props.mode === TransferLineGridMode.ScanToSubmit && isLineSplittable(data) && (
                  <Menu.Item key="split-pt-btn" onClick={() => openEditLineDialog(EPTLineActionType.Split, data)}>
                    <Label>
                      <i className="bi bi-signpost-split" />
                      &nbsp;Split
                    </Label>
                  </Menu.Item>
                )}
                <Menu.Item disabled={!isLineDeletable(data)} key="rm-batch-btn" onClick={() => deleteLine(data)}>
                  <Label>
                    <MergeCellsOutlined />
                    &nbsp;Merge
                  </Label>
                </Menu.Item>
              </Menu>
            )}
          >
            <Button size="small">
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        </div>);
      },
    },
  ];

  const actionWrapperClassName = () => {
    const cls = ['ant-input-group-wrapper', 'w-none'];

    if (props.mode === TransferLineGridMode.ScanToSubmit) {
      cls.push('btlr-10');
      cls.push('bblr-10');
    }

    return cls.join(' ');
  };

  const closeEditLineDialog = () => {
    setEditLineDialogVisible(false);
    setCurrentLine({});
    setEditLineDialogDisableFields(undefined);
  };

  const deleteLine = (line: StringKAnyVPair) => {
    Modal.confirm({
      centered: true,
      content: 'Are you sure to merge this line?',
      title: 'Confirm',
      onOk: async () => {
        const idx = lines.findIndex(l => l.postedTransferLineNum === line.postedTransferLineNum);

        if (idx > -1) {
          setIsLoading(true);
          try {
            const res = await deleteWarehousePostedTransferLine(
              line.postedTransferNum,
              line.postedTransferLineNum,
            );

            //console.log('del', res);
            if (res) {
              lines.splice(idx, 1);
              setLines([ ...lines ]);
              message.success({
                content: 'Deleted successfully',
                duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
              });
              loadDetail(props.transferNumber);
            }
          } catch(e) {
            message.error({
              content: `Deleted line error: ${e}`,
              duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
            });
          } finally {
            setIsLoading(false);
          }
        }
      },
    });
  };

  const getGridRowClassName = (row: StringKAnyVPair) => {
    const { data } = row;
    const cls: string[] = [];
    const groupOrder = data._groupOrder || 0;

    if (groupOrder % 2 === 0) {
      cls.push('datagrid-row-bg-blue-1');
    } else {
      cls.push('datagrid-row-bg-grey-1');
    }

    return cls.join(' ');
  };

  const getGroupLines = (ls: StringKAnyVPair[]) => {
    const gds: StringKAnyVPair = {};
    const gs: any[] = [];
    const ret: StringKAnyVPair[] = [];

    ls.forEach(e => {
      (gds[e.rowNum] === undefined) && (gds[e.rowNum] = []);
      (gs.indexOf(e.rowNum) < 0) && gs.push(e.rowNum);
      gds[e.rowNum].push(e);
    });
    gs.forEach((e, i) => {
      Array.isArray(gds[e]) && gds[e].forEach((l: any) => {
        l._groupOrder = i;
        ret.push(l);
      });
    });
    //ls.length > 0 && console.log('gd', gds, gs, ret);

    return ret;
  };

  const getLineActionDialogTitle = () => {
    let title = '';

    switch (currentLineActionType) {
      case EPTLineActionType.Edit:
        title = 'Edit Line';
        break;

      case EPTLineActionType.Split:
        title = 'Split Line';
        break;
    
      default:
        break;
    }

    return title;
  };

  const getLineColumns = (cols: StringKAnyVPair[]) => {
    const list: StringKAnyVPair[] = [];

    for (let i = 0; i < cols.length; i++) {
      if (!lotEnable && cols[i].name === 'lotNumber') continue;
      if (!uomEnable && cols[i].name === 'uom') continue;
      //if (!props.editable && i === cols.length - 1) continue;
      if (!isLineEditable() && i === cols.length - 1) continue;
      if (props.mode === TransferLineGridMode.ScanToSubmit) {
        if (['Pick Status'].indexOf(cols[i].header) > -1) continue;
      } else {
        if (cols[i].header === '') continue;
      }

      list.push(cols[i]);
    }

    return list;
  };

  // eslint-disable-next-line
  const getProfileSettings = async () => {
    const res1 = await getProfileSettingValue('EnableLOT', '0');
    const res2 = await getProfileSettingValue('EnableUOM', '0');

    setLotEnable(res1 === '1');
    setUomEnable(res2 === '1');
  };

  const isAllLinesPicked = () => {
    return lines.length > 0 && lines.every(e => e.pickStatus === 1);
  };

  const isAllLinesUnpicked = () => {
    return lines.length > 0 && lines.every(e => e.pickStatus !== 1);
  };

  const isLineDeletable = (line: StringKAnyVPair) => {
    const { rowNum } = line;
    let ret = false;

    line.status === 0 && rowNum > 0 && (ret = lines.filter(e => e.rowNum === rowNum).length > 1);
    //(!ret && line.quantity === 0) && (ret = true);

    return ret;
  };

  const isLineEditDataNeeded = () => {
    return props.mode === TransferLineGridMode.ScanToSubmit && currentLineActionType === EPTLineActionType.Edit;
  };

  const isLineEditable = () => {
    const { mode } = props;
    const editModes = [TransferLineGridMode.Edit, TransferLineGridMode.ScanToSubmit];

    return editModes.indexOf(mode) > -1;
  };

  const isLineSplittable = (line: StringKAnyVPair) => {
    return line.status === 0 && line.quantity > 1;
  };

  // eslint-disable-next-line
  const loadDetail = async (tn: number) => {
    setIsLoading(true);
    try {
      const detail = await fetchWarehousePostedTransferDetail(tn);

      if (detail) {
        detail.lines.forEach((e: any) => (e.status === undefined) && (e.status = detail.status));
        detail.lines.sort((a: any, b: any) => b.postedTransferLineNum - a.postedTransferLineNum);
        setLines(detail.lines);

        if (props.onLoadTransfer) props.onLoadTransfer(detail);
      }
    } catch(e) {
    } finally {
      setIsLoading(false);
    }
  };

  const onLineActionDone = (ld: StringKAnyVPair) => {
    if (currentLineActionType === EPTLineActionType.Edit) {
      const line = lines.find(e => e.postedTransferLineNum === ld.postedTransferLineNum);
      //console.log('l', line, ld);
      if (line) {
        for (const k in ld) (k in line) && (line[k] = ld[k]);
        (line.fromLocationNum > 0 && line.toLocationNum > 0) && (line.pickStatus = 1);
        setLines([...lines]);
        props.onLineChanged && props.onLineChanged(lines);
      }
    } else {
      loadDetail(props.transferNumber);
    }
  };

  const openEditLineDialog = (
    actionType: EPTLineActionType,
    ld: StringKAnyVPair,
  ) => {
    setCurrentLine(ld);
    setCurrentLineActionType(actionType);
    setEditLineDialogVisible(true);
  };

  const pickAllLines = (line: StringKAnyVPair) => {
    lines.forEach(e => e.pickStatus = 1);
    setLines([ ...lines ]);
    props.onLineChanged && props.onLineChanged(lines);
  };

  const pickLine = (line: StringKAnyVPair) => {
    setLinePickStatus(line, 1);
  };

  const renderCopyableCell = (text: string) => {
    return (
      <CopySpan
        iconWidth={20}
        inGrid
        value={text}
      />
    );
  };

  const renderLocationEditCell = (
    locationCode: string,
    data: StringKAnyVPair,
    disableFields: EditableField[],
  ) => {
    return (
      <SpaceCell
        icon={<>
          <EditOutlined 
            className="text-clickable text-info"
            onClick={() => {
              setEditLineDialogDisableFields(disableFields);
              openEditLineDialog(EPTLineActionType.Edit, data);
            }}
          />
        </>}
        iconWidth={DATAGRID_CELL_ICON_WIDTH1}
        text={locationCode}
        tooltip={locationCode}
      />
    );
  };

  const setLinePickStatus = (line: StringKAnyVPair, status: number) => {
    line.pickStatus = status;
    setLines([ ...lines ]);
    props.onLineChanged && props.onLineChanged(lines);
  };

  const unpickAllLines = (line: StringKAnyVPair) => {
    lines.forEach(e => {
      e.fromLocationCode = null;
      e.fromLocationNum = 0;
      e.toLocationCode = null;
      e.toLocationNum = 0;
      e.pickStatus = 0;
    });
    setLines([ ...lines ]);
    props.onLineChanged && props.onLineChanged(lines);
  };

  const unpickLine = (line: StringKAnyVPair) => {
    line.fromLocationCode = null;
    line.fromLocationNum = 0;
    line.toLocationCode = null;
    line.toLocationNum = 0;
    setLinePickStatus(line, 0);
  };

  React.useEffect(() => {
    if (!inited) {
      getProfileSettings();
      loadDetail(props.transferNumber);
      setInited(true);
    }

    if (version !== props.version) {
      loadDetail(props.transferNumber);
      setVersion(props.version);
    }
  }, [
    getProfileSettings,
    inited,
    loadDetail,
    props,
    version,
  ]);

  return (<>
    {<DataGrid
      columns={getLineColumns(columns)}
      //count={gridCount}
      dataSource={getGroupLines(lines)}
      //limit={gridPageSize.current}
      loading={isLoading}
      //onLimitChange={onLimitChange}
      //onSkipChange={onSkipChange}
      //pageSizes={[5, 20, 50, 100, 200]}
      pagination={false}
      rowClassName={getGridRowClassName}
      rowHeight={props.mode === TransferLineGridMode.ScanToSubmit ? 70 : 60}
      rowKey="postedTransferLineNum"
      showCellBorders="horizontal"
      //style={{height: 'calc(100vh - 240px)', maxHeight: 600}}
      style={{ height: '100%' }}
    />}
    {editLineDialogVisible && (
      <EditLineDialog
        actionType={currentLineActionType}
        disableFields={editLineDialogDisableFields}
        line={currentLine}
        maskClosable={false}
        needLineData={isLineEditDataNeeded()}
        onCancel={closeEditLineDialog}
        onSave={onLineActionDone}
        title={getLineActionDialogTitle()}
        visible={editLineDialogVisible}
        width={isLineEditDataNeeded() ? 350 :700}
      />
    )}
  </>);
};

export default TransferLineGrid;

export const exportColumns = [
  {
    header: 'SKU',
    name: 'sku',
  },
  {
    header: 'UOM',
    name: 'uom',
  },
  {
    header: 'LOT#',
    name: 'lotNumber',
  },
  {
    header: 'From Warehouse',
    name: 'fromWarehouseCode',
  },
  {
    header: 'From Location',
    name: 'fromLocationCode',
  },
  {
    header: 'Quantity',
    headerAlign: 'start',
    name: 'quantity',
    textAlign: 'end',
  },
  {
    header: 'To Warehouse',
    name: 'toWarehouseCode',
  },
  {
    header: 'To Location',
    name: 'toLocationCode',
  },
  {
    header: 'Enter By',
    name: 'createBy',
  },
  {
    header: 'Note',
    name: 'note',
  },
];

export const mapCsvData = (data: StringKAnyVPair[]) => {
  return data.map(e => {
    return { ...e, uom: `${e.uom}(${e.uomRate})` };
  });
};
